import { Modal, Button } from '@imprivata-cloud/components';
import { useTranslation } from 'react-i18next';
import { Flex, Spin } from 'antd';
import { useCallback, useState } from 'react';
import { useSelfServiceData } from '@selfservice/utils/useSelfServiceData';
import { enroll } from '@api/selfServiceServices';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { useSession } from '@common/authorization/context/Session';
import ImprivataLogo from '@assets/Imprivata_Logo.svg?react';
import { EnrollmentCompletedCard } from '../EnrollFace/EnrollmentCompletedCard/EnrollmentCompletedCard';
import { useEnrollFace } from '@selfservice/hooks/useEnrollFace';
import { ConsentScreens } from '../EnrollFace/ConsentScreens/ConsentScreens';
import '../../styles.less';
import './FaceCapture.less';
import { EnrollFlow, type EventPayload, EventType } from '@imprivata-cloud/authn';

const FaceCapture: React.FC = () => {
  const { t } = useTranslation();

  const { userInfo } = useSession();
  const { data, isLoading, isError, error } = useSelfServiceData();
  const { emitError } = useErrorContext();

  const [key, setKey] = useState(0);

  const {
    handleSubmitConsent,
    onSuccessfulFaceEnrollment,
    onCancel,
    onContinue,
    onEnrollmentDone,
    isModalOpen,
    screenState,
    onEnrollFlowError,
  } = useEnrollFace(userInfo?.userDisplayName ? userInfo?.userDisplayName : userInfo?.userUpn);

  if (isError) {
    if (error instanceof AppError) {
      emitError(error);
    } else {
      emitError(AppError.fromMessage('Error getting self service data'));
    }
  }

  const forceUpdateComponent = useCallback(() => {
    setKey((key) => key + 1);
  }, []);

  const renderContent = () => {
    if (screenState === 'welcome') {
      return (
        <ConsentScreens
          onConsent={handleSubmitConsent}
          onContinue={onContinue}
          onCancel={onCancel}
          residenceLocations={data?.residenceLocations}
        />
      );
    }

    if (screenState === 'face-enrolled') {
      return <EnrollmentCompletedCard onDone={onEnrollmentDone} />;
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="enroll-content">
        <Flex align="center" justify="center">
          <Spin />
        </Flex>
      </div>
    );
  }

  return (
    <div className="enroll-content">
      <Modal
        open={isModalOpen && screenState === 'enrollment'}
        onCancel={onCancel}
        onClose={onCancel}
        closeIcon={false}
        className="face-enroll-modal"
        destroyOnClose={true}
        wrapClassName="enroll-modal-wrapper"
        maskProps={{
          style: { backgroundColor: 'rgba(255, 255, 255, 0.5)' },
        }}
        footer={
          <div className="enroll-footer">
            <div className="enroll-cancel-button">
              <Button aria-label="enroll-cancel-button" type="text" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
            </div>
            <div className="enroll-footer-logo">
              <ImprivataLogo />
            </div>
          </div>
        }
      >
        <div className="enroll-container">
          <EnrollFlow
            key={key}
            username={data?.userInfo.username || userInfo?.userUpn || userInfo?.email || ''}
            enroll={enroll}
            isSuccessHandlerAutomatic
            reset={forceUpdateComponent}
            onSuccessfulEnrollment={onSuccessfulFaceEnrollment}
            onError={onEnrollFlowError}
            onEvent={({ event, data }: EventPayload) => {
              if (
                [EventType.CAMERA_ERROR, EventType.PERMISSION_REVOKED, EventType.DEVICE_DISCONNECTED].includes(event)
              ) {
                emitError(new AppError({ message: data?.message, code: data?.code }));
              }
            }}
            featureFlags={{ multiFaceCrop: true, fitVideoFrame: false }}
          />
        </div>
      </Modal>

      {renderContent()}
    </div>
  );
};

export default FaceCapture;
